import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { PessoaConfiguracaoCampoObrigatorioService } from '@shared/services/apis/pessoa/pessoa-configuracao-campo-obrigatorio.service';
import { ProdutoConfiguracaoCampoObrigatorioService } from '@shared/services/apis/produto/produto-configuracao-campo-obrigatorio.service';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { FuncionalidadeEnum } from '@shared/enums/usuario/funcionalidade.enum';
import { ModuloEnum } from '@shared/enums/usuario/modulo.enum';
import { TelaPessoaEnum } from '@shared/enums/pessoa/tela-pessoa.enum';
import { HeaderComponent } from '@shared/components/header/header.component';
import { FooterActionComponent } from '@shared/components/footer-action/footer-action.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { CamposObrigatoriosSecaoComponent } from '@shared/components/campos-obrigatorios-secao/campos-obrigatorios-secao.component';
import { TelaComCamposStruct } from '@shared/services/structs/financas/tela-com-campos.struct';
import { ConfiguracaoCampoObrigatorioRequest } from '@shared/services/requests/financas/configuracao-campo-obrigatorio.request';

@Component({
    selector: 'app-campos-obrigatorios-cadastros',
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        HeaderComponent,
        FooterActionComponent,
        LoadingBoxComponent,
        CamposObrigatoriosSecaoComponent
    ],
    templateUrl: './campos-obrigatorios-cadastros.component.html',
    styleUrls: ['./campos-obrigatorios-cadastros.component.css']
})
export class CamposObrigatoriosCadastrosComponent implements OnInit {

    public isLoading = false;
    public isSaving = false;
    public funcionalidadeEnum = FuncionalidadeEnum.Configuracoes_Cadastros;
    public moduloEnum = ModuloEnum.Configuracoes;
    public listaTelaComCampos: TelaComCamposStruct[] = [];
    public telaComSeletorAberto: TelaComCamposStruct | null = null;

    // Pessoa e Vendedor vêm da API.Pessoa; Produto da API.Produto. Cada API salva as suas telas.
    private listaTelaComCamposPessoa: TelaComCamposStruct[] = [];
    private listaTelaComCamposProduto: TelaComCamposStruct[] = [];

    constructor(
        private pessoaConfiguracaoCampoObrigatorioService: PessoaConfiguracaoCampoObrigatorioService,
        private produtoConfiguracaoCampoObrigatorioService: ProdutoConfiguracaoCampoObrigatorioService,
        private alertService: AlertService,
        private router: Router
    ) { }

    ngOnInit(): void {
        this.carregarConfiguracoes();
    }

    carregarConfiguracoes(): void {
        this.isLoading = true;
        forkJoin([
            this.pessoaConfiguracaoCampoObrigatorioService.getAll(),
            this.produtoConfiguracaoCampoObrigatorioService.getAll()
        ]).subscribe({
            next: ([responsePessoa, responseProduto]) => {
                this.isLoading = false;
                const respostaComErro = [responsePessoa, responseProduto].find(r => r.isError);
                if (respostaComErro) {
                    this.alertService.show('Alerta', respostaComErro.errorDescription, AlertType.warning);
                    return;
                }
                this.listaTelaComCamposPessoa = responsePessoa.listaTelaComCampos;
                this.listaTelaComCamposProduto = responseProduto.listaTelaComCampos;

                // Ordem das seções: Pessoas, Produto, Vendedor.
                this.listaTelaComCampos = [
                    ...this.listaTelaComCamposPessoa.filter(t => t.idTela === TelaPessoaEnum.Pessoa),
                    ...this.listaTelaComCamposProduto,
                    ...this.listaTelaComCamposPessoa.filter(t => t.idTela !== TelaPessoaEnum.Pessoa)
                ];
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Erro ao carregar configurações de campos obrigatórios', AlertType.error);
            }
        });
    }

    onSeletorAbertoChange(tela: TelaComCamposStruct, aberto: boolean): void {
        this.telaComSeletorAberto = aberto ? tela : null;
    }

    salvar(): void {
        this.isSaving = true;
        forkJoin([
            this.pessoaConfiguracaoCampoObrigatorioService.update(this.montarRequest(this.listaTelaComCamposPessoa)),
            this.produtoConfiguracaoCampoObrigatorioService.update(this.montarRequest(this.listaTelaComCamposProduto))
        ]).subscribe({
            next: ([responsePessoa, responseProduto]) => {
                this.isSaving = false;
                const falhas = [
                    { nome: 'Pessoa', response: responsePessoa },
                    { nome: 'Produto', response: responseProduto }
                ].filter(x => x.response.isError);
                if (falhas.length > 0) {
                    this.alertService.show('Aviso!', falhas.map(x => `${x.nome}: ${x.response.errorDescription}`).join(' | '), AlertType.warning);
                    return;
                }
                this.alertService.show('Sucesso', 'Configuração salva com sucesso!', AlertType.success);
                this.voltar();
            },
            error: () => {
                this.isSaving = false;
                this.alertService.show('Erro', 'Erro ao conectar com a API', AlertType.error);
            }
        });
    }

    private montarRequest(listaTelaComCampos: TelaComCamposStruct[]): ConfiguracaoCampoObrigatorioRequest {
        return {
            listaCampos: listaTelaComCampos
                .flatMap(tela => tela.listaCampos)
                .map(campo => ({ idCampo: campo.idCampo, isObrigatorio: campo.isObrigatorio }))
        };
    }

    voltar(): void {
        this.router.navigate(['/configuracoes/cadastros']);
    }
}
